import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { unloadingReceiptValidator } from '../../shared/trip-validator.model';
import { UnloadingSpecies } from '../../shared/unloading-species.model';
import { ConfirmDialogComponent } from '../../vessel-reg/confirm-dialog/confirm-dialog.component';
import { LookupDataService } from '../../shared/lookup-data.service';
import { LookupItem } from '../../shared/lookup-item.model';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'app-add-unloading-species-dialog',
    templateUrl: './add-unloading-species-dialog.component.html',
    styleUrls: ['./add-unloading-species-dialog.component.css'],
    standalone: false
})
export class AddUnloadingSpeciesDialogComponent implements OnInit {

  // Using a control named 'species' to hold the LookupItem selected
  speciesForm: FormGroup = new FormGroup({
    species: new FormControl(null, [Validators.required]),
    poundsLanded: new FormControl<number | null>(null, [Validators.required, Validators.min(0), Validators.max(400000)]),
    landingReceiptNumber: new FormControl('', [unloadingReceiptValidator()]),
  });

  speciesList: LookupItem[] = [];
  filteredSpecies!: Observable<LookupItem[]>;

  @ViewChild('speciesInput') speciesInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<AddUnloadingSpeciesDialogComponent>,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private lookup: LookupDataService,
    @Inject(MAT_DIALOG_DATA) public data: { species: UnloadingSpecies }
  ) { }

  ngOnInit(): void {
    // Fetch the species list from the lookup service
    this.speciesList = this.lookup.getHmsSetSpecies();

    // Set up the autocomplete filter for the species input
    this.filteredSpecies = this.speciesForm.get('species')!.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.name),
      map(name => name ? this._filterSpecies(name) : this.speciesList.slice())
    );

    // Patch form values if an existing species is being edited.
    if (this.data.species && this.data.species.speciesName) {
      const selectedSpecies = this.speciesList.find(sp => sp.name === this.data.species.speciesName);
      this.speciesForm.patchValue({
        species: selectedSpecies ? selectedSpecies : this.data.species.speciesName,
        poundsLanded: this.data.species.poundsLanded,
        landingReceiptNumber: this.data.species.landingReceipt
      });
    }
  }

  // Display function for the autocomplete
  displaySpecies(species: LookupItem): string {
    return species && species.name ? species.name : '';
  }

  // Helper function to filter the species list
  private _filterSpecies(name: string): LookupItem[] {
    const filterValue = name.toLowerCase();
    return this.speciesList.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  closeDialog() {
    this.dialogRef.close();
  }

  addSpecies() {
    if (this.speciesForm.valid) {
      let newSpecies: UnloadingSpecies = new UnloadingSpecies();
      const selectedSpecies = this.speciesForm.get('species')!.value;
      // If the selected species is an object, use its name; otherwise, use the value directly.
      newSpecies.speciesName = typeof selectedSpecies === 'object' ? selectedSpecies.name : selectedSpecies;
      newSpecies.poundsLanded = this.speciesForm.get('poundsLanded')?.value;
      newSpecies.landingReceipt = this.speciesForm.get('landingReceiptNumber')?.value;

      console.log('Species added:', newSpecies);
      this.dialogRef.close(newSpecies);
    } else {
      this.speciesForm.markAllAsTouched();
      this.toastr.warning('Errors on this form, see fields', 'Invalid');
      console.log('Form is invalid');
    }
  }

  deleteSpecies() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: { screen: 'deletespecies' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ delete: true });
      }
    });
  }

  preventInvalidInput(event: KeyboardEvent) {
    const charCode = event.key;
    if (!/^[0-9]$/.test(charCode) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Tab') {
      event.preventDefault();
    }
  }
}
